import { Box, Typography } from '@mui/material';
import MenuItem from '@mui/material/MenuItem';
import TextField from '@mui/material/TextField';
import { QueryStatus } from '@reduxjs/toolkit/query';
import React from 'react';
import { useSelector } from 'react-redux';
import { Outlet, useNavigate, useParams } from 'react-router';

import {
  customerSelector,
  useDeleteContactMutation,
  useGetContactsQuery,
  useUpdateContactMutation,
} from '@/api/v1';
import { ConfirmationModal } from '@/components/molecules/ConfirmationModal';
import { ContactDetailsSection } from '@/components/molecules/ContactDetailsSection';
import { LoadingButton } from '@/components/molecules/LoadingButton';
import countries from '@/data/countries';
import { useToast } from '@/hooks/useToast';
import { useTranslator } from '@/hooks/useTranslator';
import { ContactDetailsSectionData } from '@/types/contactDetailsSection';
import {
  businessRelationshipTypeLabels,
  Contact,
  ContactType,
  UpdateContactRequest,
} from '@/types/contacts';
import { isBusinessRelationshipType } from '@/types/contacts.ext';
import { Customer } from '@/types/customer';
import { EditComponentProps } from '@/types/editComponentProps';
import { javaEnumToSelectItem, SelectItem } from '@/types/fields';
import { Page } from '@/types/page';

export const RecipientDetailsContent: React.FC = (): React.ReactElement => {
  const { contactId = '' } = useParams();

  const tr = useTranslator();
  const navigate = useNavigate();
  const toast = useToast();

  const [updateContact, updateContactMutation] = useUpdateContactMutation();

  const { data: customer = Customer.empty() } = useSelector(customerSelector);
  const { data: recipientsPage = Page.empty<Contact>() } = useGetContactsQuery({
    beneficiaryId: customer.beneficiaryId,
    size: 1000,
  });
  const recipients = recipientsPage.content;

  const [deleteContact, deleteContactMutation] = useDeleteContactMutation();
  const [deleteConfirm, setDeleteConfirm] = React.useState<boolean>(false);

  React.useEffect((): void => {
    switch (deleteContactMutation.status) {
      case QueryStatus.rejected:
        toast.show(tr('AN_ERROR_OCCURRED'), 'error');
        break;
      case QueryStatus.fulfilled:
        toast.show(tr('DELETE_CONTACT_SUCCESS'), 'success');
        navigate('/contacts');
        break;
      default:
        // Ignore
        break;
    }
  }, [deleteContactMutation.status, navigate, toast, tr]);

  React.useEffect((): void => {
    switch (updateContactMutation.status) {
      case QueryStatus.rejected:
        toast.show(tr('AN_ERROR_OCCURRED'), 'error');
        break;
      case QueryStatus.fulfilled:
        toast.show(tr('UPDATE_CONTACT_SUCCESS'), 'success');
        break;
      default:
        // Ignore
        break;
    }
  }, [updateContactMutation.status, toast, tr]);

  // TODO: write an endpoint to fetch recipient by id
  const recipient = React.useMemo(
    (): Contact =>
      recipients.find((recipient: Contact): boolean => recipient.id === contactId) ??
      Contact.empty(),
    [recipients, contactId],
  );

  const onDelete = React.useCallback((): void => {
    setDeleteConfirm(true);
  }, []);

  const onDeleteAborted = React.useCallback((): void => {
    setDeleteConfirm(false);
  }, []);

  const recipientType = React.useMemo(
    (): Record<ContactType, string> => ({
      [ContactType.individual]: tr('INDIVIDUAL'),
      [ContactType.company]: tr('COMPANY'),
    }),
    [tr],
  );

  const country = React.useMemo(
    (): SelectItem | undefined =>
      countries.find((country: SelectItem): boolean => country.value === recipient?.country),
    [recipient],
  );

  const onDeleteConfirmed = React.useCallback((): void => {
    if (!contactId) {
      console.debug('this should never happen, contact id is required');
      return;
    }
    deleteContact(contactId);
  }, [contactId, deleteContact]);

  const recipientBusinessRelationship = React.useMemo((): string => {
    if (!recipient) {
      return '';
    }
    const { entity } = recipient;
    if (!entity) {
      return '';
    }
    return tr(businessRelationshipTypeLabels[entity.businessRelationship]);
  }, [recipient, tr]);

  const updateBusinessRelationship = React.useCallback(
    async (value: string): Promise<void> => {
      if (!isBusinessRelationshipType(value)) {
        console.debug('this should never happen, business relationship type is invalid');
        return;
      }

      const request: UpdateContactRequest = {
        id: contactId,
        data: {
          businessRelationship: value,
        },
      };

      await updateContact(request);
    },
    [contactId, updateContact],
  );

  const data = React.useMemo((): readonly ContactDetailsSectionData[] => {
    const { entity } = recipient;

    return [
      {
        key: 'RECIPIENT_TYPE',
        title: tr('RECIPIENT_TYPE'),
        values: [
          {
            key: 'RECIPIENT_TYPE',
            displayValue: recipientType[entity.type],
          },
        ],
      },
      {
        key: 'BENEFICIARY_OR_RECIPIENT_CONTACT',
        title: tr('BENEFICIARY_OR_RECIPIENT_CONTACT'),
        values: [
          {
            key: 'name',
            displayValue: Contact.getName(recipient),
          },
          {
            key: 'country',
            displayValue: tr(country?.label ?? ''),
          },
          {
            key: 'businessRelationship',
            label: tr('BUSINESS_RELATIONSHIP'),
            value: entity.businessRelationship,
            displayValue: recipientBusinessRelationship,
            editable: true,
            updateHandler: updateBusinessRelationship,
            inputComponent: BusinessRelationshipInput,
          },
        ],
      },
    ];
  }, [
    country?.label,
    recipient,
    recipientBusinessRelationship,
    recipientType,
    updateBusinessRelationship,
    tr,
  ]);

  return (
    <Box px={6} mt={4}>
      <Typography fontSize={20} fontWeight={500} lineHeight="32px">
        {tr('CONTACT_DETAILS')}
      </Typography>
      <Box display="flex" flexDirection="column" gap={4} mt={3}>
        {data.map(
          (item: ContactDetailsSectionData): React.ReactElement => (
            <ContactDetailsSection key={item.key} title={item.title} values={item.values} />
          ),
        )}
      </Box>
      <Box mt={4}>
        <LoadingButton
          variant="contained"
          color="error"
          fullWidth={true}
          loading={deleteContactMutation.isLoading}
          onClick={onDelete}>
          {tr('DELETE_CONTACT')}
        </LoadingButton>
      </Box>

      {/* Simpler modals */}
      <ConfirmationModal
        open={deleteConfirm}
        onAbort={onDeleteAborted}
        onConfirm={onDeleteConfirmed}
        confirmButtonText={tr('DELETE_CONTACT')}
        abortButtonText={tr('KEEP_CONTACT')}
        title={tr('ARE_YOU_SURE_YOU_WANT_TO_DELETE_THIS_CONTACT_TITLE')}
        description={tr('THIS_ACTION_IS_NOT_REVERSIBLE')}
      />

      {/* For modals */}
      <Outlet />
    </Box>
  );
};

const BusinessRelationshipInput = function Field(props: EditComponentProps): React.ReactElement {
  const { defaultValue, label, name } = props;
  const tr = useTranslator();

  return (
    <TextField name={name} label={label} defaultValue={defaultValue} fullWidth={true} select={true}>
      {businessRelationshipItems.map(
        (item: SelectItem): React.ReactElement => (
          <MenuItem key={item.value} value={item.value}>
            {tr(item.label)}
          </MenuItem>
        ),
      )}
    </TextField>
  );
};

const businessRelationshipItems = javaEnumToSelectItem(businessRelationshipTypeLabels);
